import { Button, Chip, CircularProgress, Stack, Typography } from '@mui/material'
import { useGetCustomers } from './hooks/useGetCustomers'
import { useCallback, useMemo, useState } from 'react'
import { GridColDef } from '@mui/x-data-grid'
import { commonDataGridStyles } from '~/ui/styles/data-grid'
import { CreateCustomerDialog } from '~/features/customers/components/CreateCustomerDialog'
import { Customer } from '~/supabase/types'
import * as R from 'remeda'
import { usePatchCustomer } from './hooks/usePatchCustomer'
import { useDeleteCustomers } from './hooks/useDeleteCustomers'
import { useDataGridProps } from '~/ui/hooks/useDataGridProps'
import { CustomDataGrid } from '~/ui/components/CustomDataGrid'
import { SelectionPanel, SelectionPanelAction } from '~/ui/components/SelectionPanel'
import { SimpleDialog } from '~/ui/components/SimpleDialog'

export const CustomerList = () => {
  const { data, isLoading } = useGetCustomers()
  const { mutateAsync: patchCustomer } = usePatchCustomer()
  const { mutate: deleteCustomers, isPending: isDeletingCustomer } = useDeleteCustomers()
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  const columns = useMemo<Array<GridColDef<Customer>>>(() => {
    return [
      {
        field: 'can_promote',
        headerName: 'Can promote',
        width: 110,
        type: 'boolean',
        editable: true,
        renderCell: (params) => {
          return (
            <Stack sx={{ alignItems: 'flex-start', flex: 1 }}>
              <Chip
                label={params.row.can_promote ? 'Yes' : 'No'}
                size="small"
                color={params.row.can_promote ? 'success' : 'default'}
              />
            </Stack>
          )
        },
      },
      {
        field: 'full_name',
        headerName: 'Name',
        width: 200,
        editable: true,
      },
      {
        field: 'email',
        headerName: 'Email',
        width: 250,
        editable: true,
      },
      {
        field: 'phone',
        headerName: 'Phone',
        width: 140,
        editable: true,
      },
      {
        field: 'country',
        headerName: 'Country',
        width: 110,
        editable: true,
      },
      {
        field: 'address',
        headerName: 'Address',
        flex: 1,
        editable: true,
      },
    ]
  }, [])

  const rows = useMemo(() => {
    if (!data) return []
    return R.pipe(
      data,
      R.sortBy((d) => d.full_name)
    )
  }, [data])

  const processRowUpdate = async (newRow: Customer, oldRow: Customer) => {
    if (R.isDeepEqual(newRow, oldRow)) return oldRow
    await patchCustomer(newRow)
    return newRow
  }

  const { selectedRows, onRowSelectionModelChange, selectionModel, reset } = useDataGridProps({
    rows,
  })

  const handleCloseDeleteDialog = useCallback(() => {
    setIsDeleteDialogOpen(false)
  }, [])

  const confirmDelete = useCallback(async () => {
    if (selectedRows.length === 0) return

    deleteCustomers(
      selectedRows.map((customer) => customer.id),
      {
        onSuccess: () => {
          setIsDeleteDialogOpen(false)
          reset()
        },
      }
    )
  }, [selectedRows, deleteCustomers, reset])

  const actions: SelectionPanelAction[] = useMemo(() => {
    return [
      {
        key: 'delete',
        Component: () => (
          <Button
            onClick={() => setIsDeleteDialogOpen(true)}
            disabled={isDeletingCustomer}
            startIcon={isDeletingCustomer ? <CircularProgress size={14} /> : null}
          >
            Delete
          </Button>
        ),
      },
    ]
  }, [isDeletingCustomer])

  return (
    <Stack flex={1}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" py={1}>
        <Typography variant="h4">Customers</Typography>
        <CreateCustomerDialog />
      </Stack>
      <SimpleDialog
        open={isDeleteDialogOpen}
        title="Delete Customer"
        text={`Are you sure you want to delete ${selectedRows.length} customer${selectedRows.length > 1 ? 's' : ''}?`}
        description={
          selectedRows.length === 1
            ? `This will permanently delete ${selectedRows[0]?.full_name || 'this customer'}.`
            : 'This action cannot be undone.'
        }
        onSubmit={confirmDelete}
        onClose={handleCloseDeleteDialog}
        isLoading={isDeletingCustomer}
        color="error"
        confirmText="Delete"
      />
      <Stack position="relative" flex={1}>
        <CustomDataGrid
          checkboxSelection
          editMode="row"
          disableRowSelectionOnClick
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={onRowSelectionModelChange}
          columns={columns}
          rows={rows}
          loading={isLoading}
          processRowUpdate={processRowUpdate}
          sx={{ ...commonDataGridStyles }}
        >
          <SelectionPanel rows={selectedRows} actions={actions} onClose={reset} />
        </CustomDataGrid>
      </Stack>
    </Stack>
  )
}
